import { useEffect, useState } from '@web-stories-wp/react';
import { applyLutToPixelData, parseLut } from './index';

const lutCache = new Map();
const LUT_PROCESSING_DEBOUNCE_MS = 120;

function getLutFormat(assetUrl) {
  const pathname = assetUrl.split(/[?#]/u)[0];
  const extension = pathname.split('.').pop()?.toLowerCase();
  return extension === 'cube' || extension === '3dl' ? extension : undefined;
}

async function loadLut(assetUrl) {
  if (!lutCache.has(assetUrl)) {
    lutCache.set(
      assetUrl,
      fetch(assetUrl).then(async (response) => {
        if (!response.ok) {
          throw new Error(`Unable to load LUT asset (${response.status}).`);
        }
        return parseLut(await response.text(), getLutFormat(assetUrl));
      })
    );
  }

  try {
    return await lutCache.get(assetUrl);
  } catch (error) {
    lutCache.delete(assetUrl);
    throw error;
  }
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error('Unable to load image for LUT processing.'));
    image.src = source;
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Unable to encode LUT-processed image.'));
      }
    }, 'image/png');
  });
}

function getBoundedCanvasDimensions(image, dimensions) {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const maximumWidth = Math.max(1, Math.floor(dimensions.width));
  const maximumHeight = Math.max(1, Math.floor(dimensions.height));
  const scale = Math.min(
    1,
    maximumWidth / sourceWidth,
    maximumHeight / sourceHeight
  );

  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
}

export async function createLutImageBlob(source, lutFilter, dimensions) {
  const intensity = Math.min(
    100,
    Math.max(0, Number(lutFilter.intensity) || 0)
  );
  if (!source || !lutFilter.assetUrl || intensity === 0) {
    return null;
  }

  const [image, lut] = await Promise.all([
    loadImage(source),
    loadLut(lutFilter.assetUrl),
  ]);
  const canvas = document.createElement('canvas');
  const canvasDimensions = getBoundedCanvasDimensions(image, dimensions);
  canvas.width = canvasDimensions.width;
  canvas.height = canvasDimensions.height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    throw new Error('Canvas 2D is unavailable for LUT processing.');
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const processed = applyLutToPixelData(imageData.data, lut, intensity);
  imageData.data.set(processed);
  context.putImageData(imageData, 0, 0);
  return canvasToBlob(canvas);
}

export function useLutImage(source, lutFilter, lutAsset, dimensions) {
  const assetUrl = lutAsset?.assetUrl;
  const targetWidth = dimensions.width;
  const targetHeight = dimensions.height;
  const intensity = Math.min(
    100,
    Math.max(0, Number(lutFilter?.intensity) || 0)
  );
  const key =
    source && assetUrl && intensity > 0
      ? `${source}:${targetWidth}x${targetHeight}:${assetUrl}:${intensity}`
      : '';
  const [state, setState] = useState({ key: '', src: null, status: 'idle' });

  useEffect(() => {
    if (!key) {
      setState({ key: '', src: null, status: 'idle' });
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;
    setState({ key, src: null, status: 'loading' });

    const timeout = setTimeout(() => {
      createLutImageBlob(
        source,
        { assetUrl, intensity },
        { width: targetWidth, height: targetHeight }
      )
        .then((blob) => {
          if (!blob || cancelled) {
            return;
          }
          objectUrl = URL.createObjectURL(blob);
          setState({ key, src: objectUrl, status: 'ready' });
        })
        .catch(() => {
          if (!cancelled) {
            setState({ key, src: null, status: 'error' });
          }
        });
    }, LUT_PROCESSING_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [assetUrl, intensity, key, source, targetHeight, targetWidth]);

  return state.key === key
    ? state
    : { key, src: null, status: key ? 'loading' : 'idle' };
}

export function clearLutCacheForTests() {
  lutCache.clear();
}
